"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { formatShortcutLabel, useIsMacLike } from "@/lib/platformShortcuts";
import { getRecentTools } from "@/lib/recent-tools";
import { getFavoriteTools, toggleFavoriteTool } from "@/lib/favorite-tools";
import Link from "next/link";
import { ArrowRight, ArrowUpRight, Box, Car, CheckCircle2, Code2, Lock, Settings, Sparkles, Star } from "lucide-react";
import { AppHeader } from "@/components/layout/AppHeader";
import { LandingShowcaseCarousel } from "@/components/landing/LandingShowcaseCarousel";
import { LegalFooter } from "@/components/LegalFooter";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToolAccess } from "@/lib/home/planPreview";
import { HubBadge } from "@/components/hub/HubBadge";
import { hasFeature, lockedToolHref } from "@/lib/billing/planLabel";
import { cn } from "@/lib/utils";
import { studioLockCopy, assetCategoryToolRank, HUB_ASSET_TAGS, HUB_ASSET_TILES, HUB_FREE_TOOLS, SUBSCRIBER_HUB_TOOLS, type HubAssetTag, type HubTool } from "@/components/hub/hubData";
import { ToolShowcaseCard } from "@/components/hub/ToolShowcaseCard";
import { justShippedRank } from "@/components/home/NewFeatureRow";
import { NewToolPopup } from "@/components/home/NewToolPopup";
import { WRENCH_GOALS, type WrenchGoal } from "@/components/wrench/routes";
import { useServiceSettings } from "@/hooks/useSiteSettings";
import { useRecentScroll } from "@/hooks/useRecentScroll";
import { resolveToolSortOrder } from "@/lib/toolDisplayOrder";
import { resolveToolPreviewImage } from "@/lib/toolPreviewImages";
import { DISCORD_URL } from "@/components/landing/homepage-data";

const GOAL_ICONS: Record<WrenchGoal["icon"], React.ComponentType<{ className?: string }>> = {
  props: Box,
  livery: Car,
  optimize: Settings,
  scripts: Code2,
};

function deriveCreatorName(user: ReturnType<typeof useAuth>["user"]): string | null {
  if (!user) return null;
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const candidates = [
    typeof meta.display_name === "string" ? meta.display_name : null,
    typeof meta.full_name === "string" ? meta.full_name : null,
    typeof meta.name === "string" ? meta.name : null,
    typeof meta.user_name === "string" ? meta.user_name : null,
    typeof meta.preferred_username === "string" ? meta.preferred_username : null,
    typeof user.email === "string" ? user.email.split("@")[0] : null,
  ];
  return candidates.find((v): v is string => !!v && v.length > 0) ?? null;
}

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 127.14 96.36" fill="currentColor" aria-hidden="true">
      <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z" />
    </svg>
  );
}

/**
 * Homepage for signed-in subscribers. Same overall shape as the marketing
 * landing (nav, hero, sections, footer) but the copy + CTAs lean toward
 * "pick up a tool and ship something" rather than "convince me to subscribe."
 */
export default function CreatorHome() {
  const { user } = useAuth();
  const name = useMemo(() => deriveCreatorName(user), [user]);

  return (
    <div className="lb-page min-h-screen">
      <AppHeader />
      <main>
        <Hero name={name} />
        {/*
          No "new tools" announcement band here. A subscriber already owns the
          tools, so recently-shipped ones (the rotating NEW_FEATURE_TOOLS set)
          instead float to the front of the All-tools grid below and wear a
          distinct accent "Just shipped" marker — awareness lives exactly where
          they pick a tool, at zero cost in vertical space. The full banner row
          still runs on the marketing landing to excite prospects.
        */}
        <FavoritesSection />
        <Goals />
        <RecentSection />
        <ToolsSection />
        {/* Near the bottom so it never interrupts the work-focused flow above. */}
        <LandingShowcaseCarousel
          eyebrow="Community Showcase"
          title="See what the community is making"
          subtitle="Published work from other Workshop creators. Share your own from the apps."
        />
      </main>
      <LegalFooter />
      <NewToolPopup />
    </div>
  );
}

function Hero({ name }: { name: string | null }) {
  const isMacLike = useIsMacLike();
  return (
    <section className="relative overflow-hidden">
      <div className="lb-container py-16 sm:py-24">
        <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-muted-foreground">
          Welcome back
        </p>
        <h1 className="lb-display mt-3 text-[3.4rem] font-semibold text-foreground sm:text-[4.6rem]">
          {name ? <>Hello, <span className="text-accent">{name}</span>.</> : <>Pick up where you left off.</>}
        </h1>
        <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
          Jump straight into a tool, route through Wrench, or open the Hub to see everything at a glance.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            href="#tools"
            className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background shadow-sm transition hover:-translate-y-0.5 hover:bg-foreground/90 hover:shadow-md"
          >
            Browse tools
            <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={() => {
              // Trigger Wrench's command palette via the global ⌘K shortcut.
              if (typeof window === "undefined") return;
              window.dispatchEvent(
                new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }),
              );
            }}
            className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/[0.06] px-5 py-2.5 text-sm font-medium text-foreground/80 transition hover:-translate-y-0.5 hover:border-accent/50 hover:bg-accent/10 hover:text-foreground"
          >
            <Sparkles className="h-4 w-4 text-accent" />
            Search
            <span className="ml-0.5 inline-flex items-center gap-0.5 rounded-md border border-border/70 bg-background/80 px-1.5 py-0.5 font-mono text-[10.5px] font-semibold text-foreground/70 shadow-[0_1px_0_hsl(var(--foreground)/0.06)]">
              {formatShortcutLabel("⌘K", isMacLike)}
            </span>
          </button>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/35 bg-[#5865F2]/10 px-5 py-2.5 text-sm font-medium text-foreground/85 transition hover:-translate-y-0.5 hover:border-[#5865F2]/55 hover:bg-[#5865F2]/15 hover:text-foreground dark:border-[#5865F2]/45 dark:bg-[#5865F2]/12 dark:hover:bg-[#5865F2]/18"
          >
            <DiscordIcon className="h-4 w-4 text-[#5865F2] dark:text-[#8EA1FF]" />
            Join our Discord
          </a>
        </div>
      </div>
    </section>
  );
}

function Goals() {
  return (
    <section id="goals" className="lb-container pb-12 sm:pb-16">
      <SectionHeader eyebrow="Quick start" title="What are you building today?" />
      <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {WRENCH_GOALS.map((goal) => {
          const Icon = GOAL_ICONS[goal.icon];
          return (
            <Link
              key={goal.id}
              href={goal.destination}
              className="group flex items-start gap-3 rounded-2xl border border-border/70 bg-card/70 p-5 transition duration-200 hover:-translate-y-0.5 hover:border-accent/40 hover:bg-accent/5"
            >
              <span className="grid h-11 w-11 flex-none place-items-center rounded-xl border border-border/70 bg-secondary/60 text-foreground/85 transition group-hover:border-accent/35 group-hover:text-accent">
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold tracking-tight text-foreground">{goal.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Open {goal.destination.replace(/^\//, "")}
                </p>
              </div>
              <ArrowUpRight className="h-4 w-4 flex-none text-muted-foreground/0 transition group-hover:text-accent" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/** Subscribes to favorites changes (toggle from any card on the page). */
function useFavoriteHrefs(): [Set<string>, (href: string) => void] {
  const [hrefs, setHrefs] = useState<string[]>([]);
  useEffect(() => {
    setHrefs(getFavoriteTools());
    const onChange = () => setHrefs(getFavoriteTools());
    window.addEventListener("workshop-favorites-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("workshop-favorites-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  const set = useMemo(() => new Set(hrefs), [hrefs]);
  const toggle = useCallback((href: string) => {
    toggleFavoriteTool(href);
  }, []);
  return [set, toggle];
}

function FavoritesSection() {
  const { permissions, isAdmin } = useToolAccess();
  const { data: serviceSettings = [] } = useServiceSettings();
  const [favSet] = useFavoriteHrefs();

  const toolByHref = useMemo(() => {
    const map = new Map<string, HubTool>();
    for (const tool of SUBSCRIBER_HUB_TOOLS) map.set(tool.href, tool);
    return map;
  }, []);

  const favoriteTools = useMemo(
    () =>
      Array.from(favSet)
        .map((h) => toolByHref.get(h))
        .filter((t): t is HubTool => Boolean(t))
        .slice(0, 4),
    [favSet, toolByHref],
  );

  if (favoriteTools.length === 0) return null;

  return (
    <section className="lb-container pb-10">
      <div className="flex items-baseline justify-between gap-3 border-b border-border/50 pb-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-foreground/85">
          Favorites
        </span>
        <span className="truncate text-[11px] text-muted-foreground/80">Pinned at the top.</span>
      </div>
      <div className="mt-4 grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {favoriteTools.map((tool) => (
          <ToolShowcaseCard
            key={tool.href}
            tool={tool}
            locked={!hasFeature(permissions, tool.featureKey, { isAdmin })}
            imageUrl={resolveToolPreviewImage({
              href: tool.href,
              featureKey: tool.featureKey,
              serviceSettings,
            })}
          />
        ))}
      </div>
    </section>
  );
}

function RecentSection() {
  const { permissions, isAdmin } = useToolAccess();
  const { data: serviceSettings = [] } = useServiceSettings();
  const [recentHrefs, setRecentHrefs] = useState<string[]>([]);
  useEffect(() => {
    setRecentHrefs(getRecentTools());
  }, []);

  // Build a flat lookup from href → HubTool so we can resolve each visited href.
  const toolByHref = useMemo(() => {
    const map = new Map<string, HubTool>();
    for (const tool of SUBSCRIBER_HUB_TOOLS) map.set(tool.href, tool);
    return map;
  }, []);

  const recentTools = useMemo(
    () =>
      recentHrefs
        .map((href) => toolByHref.get(href))
        .filter((t): t is HubTool => Boolean(t))
        .slice(0, 4),
    [recentHrefs, toolByHref],
  );

  if (recentTools.length === 0) return null;

  return (
    <section className="lb-container pb-10">
      <div className="flex items-baseline justify-between gap-3 border-b border-border/50 pb-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-foreground/85">
          Recent
        </span>
        <span className="truncate text-[11px] text-muted-foreground/80">Tools you opened lately.</span>
      </div>
      <div className="mt-4 grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {recentTools.map((tool) => (
          <ToolShowcaseCard
            key={tool.href}
            tool={tool}
            locked={!hasFeature(permissions, tool.featureKey, { isAdmin })}
            imageUrl={resolveToolPreviewImage({
              href: tool.href,
              featureKey: tool.featureKey,
              serviceSettings,
            })}
          />
        ))}
      </div>
    </section>
  );
}

// One-line description per asset segment, shown beside each group heading in
// the "All" view (mirrors the hint slot the hub's category headings use).
const ASSET_HINTS: Record<HubAssetTag, string> = {
  all: "Everything in one place.",
  vehicles: "Liveries, meta, lights, and textures.",
  props: "Generate 3D models and item art.",
  clothing: "EUP outfits and add-on packs.",
  weapons: "Weapon textures and 3D previews.",
  scripts: "Audit, package, and document resources.",
};

function ToolsSection() {
  const { permissions, isAdmin } = useToolAccess();
  const { data: serviceSettings = [] } = useServiceSettings();

  // The hub's asset segmentation (Vehicles / Clothing / Props / Weapons /
  // Scripts) lives in HUB_ASSET_TILES — reuse it as the single source of truth
  // for "what kind of asset does this tool make".
  const hrefToAssetTag = useMemo(() => {
    const map = new Map<string, HubAssetTag>();
    for (const tile of HUB_ASSET_TILES) {
      const tag = tile.tags.find((t) => t !== "all");
      if (tag) map.set(tile.href, tag);
    }
    for (const tool of HUB_FREE_TOOLS) map.set(tool.href, tool.assetTag);
    return map;
  }, []);

  // Tools grouped by the asset they produce, in HUB_ASSET_TAGS order. Within
  // every group, recently-shipped tools (the rotating NEW_FEATURE_TOOLS set)
  // float to the front, newest-first — this is where a subscriber discovers
  // what's new, in place of a band above the page — then the admin sort_order,
  // then declaration order. The boost rewrites nothing and disappears on its
  // own once a tool graduates out of the "new" set.
  const categories = useMemo(() => {
    // Free tools ride along as ordinary, unbadged tools (see HUB_FREE_TOOLS).
    const tools = SUBSCRIBER_HUB_TOOLS;
    const declOrder = (tool: HubTool) => tools.indexOf(tool);
    return HUB_ASSET_TAGS.filter((tag) => tag.id !== "all")
      .map((tag) => ({
        id: tag.id,
        label: tag.label,
        hint: ASSET_HINTS[tag.id],
        tools: tools
          .filter((tool) => hrefToAssetTag.get(tool.href) === tag.id)
          .sort((a, b) => {
            // Owner-curated order (ASSET_CATEGORY_TOOL_ORDER) is authoritative for
            // any tool placed in it. A genuinely new tool not yet woven into that
            // list still floats to the front (preserves "just shipped surfaces
            // inline"); everything else falls back to admin sort_order, then
            // declaration order. Categories with no curated list (props/weapons/
            // scripts) keep their existing new-first → admin → declaration sort.
            const aCur = assetCategoryToolRank(tag.id, a.href);
            const bCur = assetCategoryToolRank(tag.id, b.href);
            const aNew = justShippedRank(a.href);
            const bNew = justShippedRank(b.href);
            const aFloat = aNew !== -1 && aCur === -1;
            const bFloat = bNew !== -1 && bCur === -1;
            if (aFloat !== bFloat) return aFloat ? -1 : 1;
            if (aFloat && bFloat) return aNew - bNew;
            if (aCur !== bCur) {
              if (aCur === -1) return 1;
              if (bCur === -1) return -1;
              return aCur - bCur;
            }
            const aOrder = resolveToolSortOrder({ href: a.href, featureKey: a.featureKey, serviceSettings });
            const bOrder = resolveToolSortOrder({ href: b.href, featureKey: b.featureKey, serviceSettings });
            if (aOrder !== bOrder) return aOrder - bOrder;
            return declOrder(a) - declOrder(b);
          }),
      }))
      .filter((cat) => cat.tools.length > 0);
  }, [serviceSettings, hrefToAssetTag]);
  const allTools = useMemo(() => categories.flatMap((c) => c.tools), [categories]);

  const [activeCategory, setActiveCategory] = useState<HubAssetTag>("all");
  const visibleCategories = useMemo(
    () => (activeCategory === "all" ? categories : categories.filter((c) => c.id === activeCategory)),
    [activeCategory, categories],
  );
  const visibleTools = useMemo(() => visibleCategories.flatMap((c) => c.tools), [visibleCategories]);

  const [activeTool, setActiveTool] = useState<HubTool>(allTools[0]);
  const displayLocked = !hasFeature(permissions, activeTool.featureKey, { isAdmin });
  const activeImageUrl = useMemo(
    () =>
      resolveToolPreviewImage({
        href: activeTool.href,
        featureKey: activeTool.featureKey,
        serviceSettings,
      }),
    [activeTool.featureKey, activeTool.href, serviceSettings],
  );

  // Keep the detail panel on a visible tool when the active filter changes.
  useEffect(() => {
    if (!visibleTools.some((tool) => tool.href === activeTool.href)) {
      setActiveTool(visibleTools[0] ?? allTools[0]);
    }
  }, [activeTool.href, visibleTools, allTools]);

  const tabs: { id: HubAssetTag; label: string; count: number }[] = [
    { id: "all", label: "All", count: allTools.length },
    ...categories.map((c) => ({ id: c.id, label: c.label, count: c.tools.length })),
  ];

  // Hover switches the active segment instantly — but only when the cursor is
  // genuinely moving. Suppress the switch mid-scroll, otherwise the tab row
  // sliding under a stationary pointer fires mouseenter and collapses the grid.
  const scrolledRecently = useRecentScroll();

  return (
    <section id="tools" className="lb-container pb-16 sm:pb-24">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <SectionHeader eyebrow="All tools" title="Every Workshop tool, organized." />
      </div>

      {/* Category segmented control — hover (or focus/tap) to switch */}
      <div className="mb-6 flex">
        <div
          role="tablist"
          aria-label="Filter tools by category"
          className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-2xl border border-border/70 bg-card/60 p-1"
        >
          {tabs.map((tab) => {
            const isActive = activeCategory === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onMouseEnter={() => {
                  if (!scrolledRecently()) setActiveCategory(tab.id);
                }}
                onFocus={() => setActiveCategory(tab.id)}
                onClick={() => setActiveCategory(tab.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 text-[13px] font-medium transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                  isActive
                    ? "bg-accent text-accent-foreground shadow-sm shadow-accent/20"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {tab.label}
                <span
                  className={cn(
                    "font-mono text-[10px] leading-none tabular-nums",
                    isActive ? "text-accent-foreground/70" : "text-muted-foreground/70",
                  )}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Split layout: grid + detail panel */}
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[1fr_520px]">
        {/* Tool grid, grouped by category */}
        <div className="space-y-8">
          {visibleCategories.map((cat, catIdx) => {
            const isLast = catIdx === visibleCategories.length - 1;
            return (
              <div key={cat.id} className="space-y-3">
                {activeCategory === "all" && (
                  <div className="flex items-baseline justify-between gap-3 border-b border-border/50 pb-2">
                    <span className="flex items-baseline gap-2">
                      <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-foreground/85">
                        {cat.label}
                      </span>
                      <span className="font-mono text-[10px] tabular-nums text-muted-foreground/70">
                        {cat.tools.length}
                      </span>
                    </span>
                    <span className="truncate text-[11px] text-muted-foreground/80">{cat.hint}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 [grid-auto-rows:minmax(12rem,auto)]">
                  {cat.tools.map((tool) => (
                    <GridCard
                      key={tool.href}
                      tool={tool}
                      locked={!hasFeature(permissions, tool.featureKey, { isAdmin })}
                      onHover={setActiveTool}
                    />
                  ))}
                  {activeCategory === "all" && isLast && (
                    <div className="flex h-full flex-col gap-3 rounded-2xl border border-dashed border-border/40 p-4 opacity-50">
                      <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-border/50 bg-secondary/30">
                        <Sparkles className="h-4 w-4 text-muted-foreground/50" />
                      </span>
                      <div className="flex-1">
                        <p className="text-base font-semibold tracking-tight text-muted-foreground">Coming Soon</p>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground/60">More tools are in development.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel — desktop only. Sticky offset clears the 57px sticky
            header (matches the scroll-mt-20 convention) so the header never
            paints over the panel's top when scrolled. */}
        <div className="hidden lg:block lg:sticky lg:top-20">
          <DetailPanel tool={activeTool} locked={displayLocked} imageUrl={activeImageUrl} />
        </div>
      </div>
    </section>
  );
}

// ─── Tool tags (badge cluster) ───────────────────────────────────────────────

function LockedChip() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-secondary/40 px-1.5 py-px font-mono text-[8.5px] uppercase tracking-[0.18em] text-muted-foreground">
      <Lock className="h-2.5 w-2.5" />
      Locked
    </span>
  );
}

function JustShippedChip() {
  // Green recency marker matching the landing "New" badge, so a recently-shipped
  // tool reads as new at a glance and stays consistent across surfaces.
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--success)/0.45)] bg-[hsl(var(--success)/0.15)] px-1.5 py-px font-mono text-[8.5px] uppercase tracking-[0.18em] text-success">
      <Sparkles className="h-2.5 w-2.5" />
      New
    </span>
  );
}

/**
 * Tool tag cluster. Capability tags (AI, Beta) render ALONGSIDE the recency tag,
 * so a tool can be both e.g. "AI" and "Just shipped" at once. The evergreen
 * green "New" badge is suppressed while a tool is just-shipped (the accent
 * marker says it better). When locked, the Locked chip shows, still paired with
 * the recency chip so a just-shipped tool above the viewer's plan reads as new.
 */
function ToolTags({
  locked,
  justShipped,
  badge,
}: {
  locked: boolean;
  justShipped: boolean;
  badge?: HubTool["badge"];
}) {
  if (locked) {
    return (
      <>
        {justShipped && <JustShippedChip />}
        <LockedChip />
      </>
    );
  }
  return (
    <>
      {(badge === "ai" || badge === "beta") && <HubBadge kind={badge} />}
      {justShipped ? <JustShippedChip /> : badge === "new" ? <HubBadge kind="new" /> : null}
    </>
  );
}

// ─── Grid card (used inside ToolsSection) ────────────────────────────────────

function GridCard({
  tool,
  locked,
  onHover,
}: {
  tool: HubTool;
  locked: boolean;
  onHover: (tool: HubTool) => void;
}) {
  const Icon = tool.icon;
  const href = locked ? lockedToolHref(tool.featureKey) : tool.href;
  const [favSet, toggle] = useFavoriteHrefs();
  const favorited = favSet.has(tool.href);
  // A tool in the rotating NEW_FEATURE_TOOLS set wears the "New" chip and floats
  // to the front of its group, locked or not. It gets no border treatment of its
  // own: an always-on accent ring read as a hover highlight that never went away.
  const justShipped = justShippedRank(tool.href) !== -1;

  return (
    <div className="group relative h-full">
    <Link
      href={href}
      onMouseEnter={() => onHover(tool)}
      onFocus={() => onHover(tool)}
      // Highlight is hover/keyboard-focus only. The card driving the detail
      // panel is NOT styled as selected: that state outlives the pointer, so it
      // read as a highlight stuck on the last card touched (and on every
      // breakpoint below lg there is no panel for it to point at).
      className="relative flex h-full min-h-[12rem] flex-col gap-3 rounded-2xl border border-border/70 bg-card/80 p-4 transition duration-200 hover:border-accent/40 hover:bg-accent/5 focus-visible:border-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
    >
      {/* Icon */}
      <span
        className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-border/70 bg-secondary/60 text-foreground/85 transition group-hover:border-accent/35 group-hover:text-accent"
      >
        <Icon className="h-5 w-5" />
      </span>

      {/* Locked: same hover/focus unlock overlay as ToolShowcaseCard. The whole
          card already links to the upgrade page, so touch devices lose nothing. */}
      {locked && (
        <div className="absolute inset-0 z-10 grid place-items-center rounded-2xl bg-background/80 opacity-0 backdrop-blur-[2px] transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
          <div className="flex flex-col items-center gap-1.5 px-3 text-center">
            <span className="grid h-9 w-9 place-items-center rounded-xl border border-border/70 bg-background/85 text-foreground/80">
              <Lock className="h-4 w-4" />
            </span>
            <span className="text-xs font-semibold tracking-tight text-foreground">
              {studioLockCopy(tool.featureKey)?.cta ?? "Upgrade to unlock"}
            </span>
            {(tool.lockedNote ?? studioLockCopy(tool.featureKey)?.note) && (
              <span className="max-w-[15rem] text-[11px] leading-snug text-muted-foreground">
                {tool.lockedNote ?? studioLockCopy(tool.featureKey)?.note}
              </span>
            )}
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-[11px] font-semibold text-accent-foreground">
              See plans
            </span>
          </div>
        </div>
      )}

      {/* Explore label */}
      {!locked && (
        <span className="absolute bottom-3 right-3 flex items-center gap-1 translate-x-2 text-[11px] font-medium text-muted-foreground opacity-0 transition duration-300 group-hover:translate-x-0 group-hover:opacity-100">
          Explore Tool
          <ArrowRight className="h-3 w-3" />
        </span>
      )}

      {/* Title + format tag + description */}
      <div className="min-h-0 flex-1">
        <div className="mb-1.5">
          {/* Wraps so a long title plus two chips (e.g. "Embroidery Tool" + AI
              + New) drops the chips to the next line instead of overflowing
              the card. pr-6 keeps the title clear of the favorite star. */}
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pr-6">
            <span className="min-w-0 text-base font-semibold tracking-tight text-foreground">
              {tool.title}
            </span>
            <ToolTags locked={locked} justShipped={justShipped} badge={tool.badge} />
          </span>
        </div>
        <p className="line-clamp-3 text-[12.5px] leading-relaxed text-muted-foreground">
          {tool.blurb}
        </p>
        {tool.fileTags && tool.fileTags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1 pb-5">
            {tool.fileTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded border border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] px-1.5 py-px font-mono text-[9px] tracking-wide text-success"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
    {!locked && (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle(tool.href);
        }}
        aria-label={favorited ? `Remove ${tool.title} from favorites` : `Favorite ${tool.title}`}
        title={favorited ? "Unfavorite" : "Favorite"}
        className={cn(
          "absolute right-2.5 top-2.5 grid h-6 w-6 place-items-center rounded-md transition",
          favorited
            ? "text-amber-400 hover:bg-amber-400/10"
            : "text-muted-foreground/40 opacity-0 hover:bg-muted/40 hover:text-foreground group-hover:opacity-100 focus:opacity-100",
        )}
      >
        <Star className={cn("h-3.5 w-3.5", favorited ? "fill-current" : "")} />
      </button>
    )}
    </div>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function DetailPanel({
  tool,
  locked,
  imageUrl,
}: {
  tool: HubTool;
  locked: boolean;
  imageUrl: string | null;
}) {
  const Icon = tool.icon;
  const href = locked ? lockedToolHref(tool.featureKey) : tool.href;
  // Mirror the grid's "Just shipped" marker so the signal follows the tool into
  // the detail panel rather than reverting to its evergreen badge.
  const justShipped = justShippedRank(tool.href) !== -1;

  return (
    <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/90">
      <div key={tool.href} className="animate-in fade-in slide-in-from-bottom-2 duration-200">
      {/* Preview area */}
      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-secondary/80 via-card/80 to-card">
        {imageUrl ? (
          <>
            <img src={imageUrl} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card/80 to-transparent" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_40%,_var(--tw-gradient-stops))] from-accent/8 via-transparent to-transparent" />
            <div className="flex h-full items-center justify-center">
              <Icon className="h-28 w-28 text-foreground/[0.06]" />
            </div>
          </>
        )}
        {locked && (
          <div className="absolute inset-0 grid place-items-center bg-background/55 backdrop-blur-[2px]">
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="grid h-11 w-11 place-items-center rounded-2xl border border-border/70 bg-background/85 text-foreground/80">
                <Lock className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold tracking-tight text-foreground">
                {studioLockCopy(tool.featureKey)?.cta ?? "Upgrade to unlock"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-4 p-5">
        {/* Title */}
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-2xl font-bold tracking-tight text-foreground">{tool.title}</h3>
            <ToolTags locked={locked} justShipped={justShipped} badge={tool.badge} />
          </div>
        </div>

        {/* Description */}
        <p className="text-sm leading-relaxed text-muted-foreground">{tool.blurb}</p>

{/* Bullets */}
        {tool.bullets && tool.bullets.length > 0 && (
          <ul className="space-y-1.5">
            {tool.bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-none text-accent" />
                {bullet}
              </li>
            ))}
          </ul>
        )}

        {/* CTA */}
        <div className="flex items-center gap-3 pt-1">
          <Link
            href={href}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground transition hover:opacity-90"
          >
            {locked ? (
              <>
                <Lock className="h-3.5 w-3.5" />
                {studioLockCopy(tool.featureKey)?.cta ?? "Upgrade to unlock"}
              </>
            ) : (
              <>
                Open {tool.title}
                <ArrowRight className="h-3.5 w-3.5" />
              </>
            )}
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
}

// ─── Shared section header ────────────────────────────────────────────────────

function SectionHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
        {eyebrow}
      </p>
      <h2 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-foreground sm:text-[2rem]">
        {title}
      </h2>
    </div>
  );
}
